import { inject, Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { first, Observable } from 'rxjs';
import { delay, filter, map, mergeMap, switchMap, tap, timeout, withLatestFrom } from 'rxjs/operators';

import { UISocketService } from '../../../shared/services/ui.socket.service';
import { ExecutionStatus } from '../../models/Execution';
import { UpdateDeviceCommand } from '../../models/UpdateDeviceCommand';
import { BoxActions } from '../actions/box.actions';
import { DeviceActions } from '../actions/device.actions';
import { SiteDetailPageActions } from '../actions/site-detail-page.actions';
import {
    addDeviceSectionToSync,
    addDeviceStateToSync,
    clearDeviceSectionSyncState,
    removeDeviceStateToSync,
    updateDeviceStateToSyncExecutionStatus,
} from '../actions/ui.actions';
import { getRawDevice } from '../selectors/device-detail';
import { getDeviceUpdateTask, getUISiteId } from '../selectors/ui';

export const DEVICE_UPDATE_COMPLETION_DELAY = new InjectionToken<number>('DEVICE_UPDATE_COMPLETION_DELAY', {
    providedIn: 'root',
    factory: () => 60 * 1000,
});

@Injectable({ providedIn: 'root' })
export class SiteDetailTasksEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly uiSocketService = inject(UISocketService);

    constructor(@Optional() @Inject(DEVICE_UPDATE_COMPLETION_DELAY) private completionDelay: number = 60 * 1000) {}

    initTaskWhenSectionRefreshStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.settingsRefreshStarted, SiteDetailPageActions.diagnosticSectionRefreshStarted),
            withLatestFrom(this.store.select(getUISiteId)),
            tap(([{ pendingTasks }]) => this.uiSocketService.setExecutions(pendingTasks.map((task) => task.job_id))),
            map(([{ deviceId, sectionTitle, pendingTasks }, siteId]) =>
                addDeviceSectionToSync({
                    siteId,
                    deviceId,
                    sectionTitle,
                    executions: pendingTasks,
                }),
            ),
        ),
    );

    clearSectionRefreshOnOutcome$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.sectionRefreshCompleted, SiteDetailPageActions.sectionRefreshAborted),
            map(({ siteId, deviceId, sectionTitle }) =>
                clearDeviceSectionSyncState({ siteId, deviceId, sectionTitle }),
            ),
        ),
    );

    initTrackingForDeviceUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SiteDetailPageActions.deviceUpdateRequested),
            mergeMap((updateRequest) =>
                this.store.select(getRawDevice(updateRequest.deviceId)).pipe(
                    first(Boolean),
                    map((device) =>
                        addDeviceStateToSync({
                            siteId: device.site_id,
                            deviceId: device.id,
                            state: {
                                stateLabel: updateRequest.property.label,
                                oldValue: updateRequest.initialValue,
                                newValue: updateRequest.newValue,
                                stateName: updateRequest.property.id,
                            },
                        }),
                    ),
                ),
            ),
        ),
    );

    updateTaskWhenUpdateStateHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.deviceUpdateStarted),
            map(({ pendingTask: execution, command: { device } }) => {
                this.uiSocketService.setExecutions([execution.job_id]);
                return updateDeviceStateToSyncExecutionStatus({ siteId: device.site_id, execution });
            }),
        ),
    );

    updateDeviceStateUpdateTaskWhenFailed$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.deviceUpdateFailed),
            this.waitForDeviceUpdateTask(),
            map((updateCommand) =>
                updateDeviceStateToSyncExecutionStatus({
                    siteId: updateCommand.device.site_id,
                    execution: {
                        deviceId: updateCommand.device.id,
                        state: updateCommand.property.id,
                        status: ExecutionStatus.FAILED,
                        job_id: '',
                    },
                }),
            ),
        ),
    );

    removeUpdateStateTaskWhenUpdateCompleted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DeviceActions.deviceUpdateCompleted),
            switchMap((updateData) =>
                this.store.select(getDeviceUpdateTask(updateData.deviceId, updateData.stateId)).pipe(first(Boolean)),
            ),
            delay(this.completionDelay),
            map((updateTask) => removeDeviceStateToSync({ jobId: updateTask.job_id })),
        ),
    );

    initTrackingForRebootBox$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BoxActions.boxRebootRequested),
            switchMap(({ deviceId }) => this.store.select(getRawDevice(deviceId)).pipe(first(Boolean))),
            map(({ id: deviceId, site_id: siteId }) =>
                addDeviceStateToSync({
                    siteId,
                    deviceId,
                    state: { stateLabel: 'REBOOT_BOX', stateName: 'box_status' },
                }),
            ),
        ),
    );

    updateTaskWhenRebootBoxHasStarted$ = createEffect(() =>
        this.actions$.pipe(
            ofType(BoxActions.boxRebootStarted),
            tap(({ pendingTask }) => this.uiSocketService.setExecutions([pendingTask.job_id])),
            map(({ command, pendingTask }) =>
                updateDeviceStateToSyncExecutionStatus({
                    siteId: command.device.site_id,
                    execution: {
                        state: 'box_status',
                        job_id: pendingTask.job_id,
                        deviceId: command.device.id,
                        status: ExecutionStatus.INITIALIZED,
                    },
                }),
            ),
        ),
    );

    private waitForDeviceUpdateTask() {
        return (source: Observable<UpdateDeviceCommand>) =>
            source.pipe(
                mergeMap((updateCommand) =>
                    this.store.select(getDeviceUpdateTask(updateCommand.device.id, updateCommand.property.id)).pipe(
                        map((task) => (task ? updateCommand : null)),
                        filter((updateCommand) => updateCommand !== null),
                        first(),
                        timeout(1000),
                    ),
                ),
            );
    }
}
